/**
 * The forgot-password screen (epic 103 D4, task 04) — the unauthenticated
 * request leg of the reset flow, mounted OUTSIDE the Layout beside /login.
 * The api answers EVERY request with the same generic 200 (no account
 * oracle), so the confirmation frame is client-fixed and identical for
 * every address — known, unknown or silently throttled (the server owns
 * rate limiting; there is no rate-limit UI here). Repeated requests stay
 * possible: the frame returns to the form with the address preserved, and
 * the button disables only while a request is in flight. Client-side
 * validation is non-empty only — the api stays the authority.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

/** Outcome of the request. `sent` is any 2xx (the api's always-generic 200);
 * `failed` is a network failure or a 5xx — stated honestly, never as a
 * claim about the account. */
type RequestOutcome = 'sent' | 'failed';

async function requestResetEmail(email: string): Promise<RequestOutcome> {
  try {
    // Unauthenticated: these screens own their unauth UX, so like the other
    // auth calls they opt out of the 401 → /login redirect.
    const response = await apiFetch(`${API_BASE_URL}/admin/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
      skipAuthRedirect: true,
    });
    return response.ok ? 'sent' : 'failed';
  } catch {
    return 'failed';
  }
}

/** Checked trimmed — the screen posts the trimmed value (login-screen idiom). */
function emailHint(value: string): string | null {
  return value.trim() === '' ? 'Enter your email.' : null;
}

export function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    const nextHint = emailHint(email);
    if (nextHint !== null) {
      setError(null);
      setHint(nextHint);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const outcome = await requestResetEmail(email.trim());
      if (outcome === 'sent') {
        setSent(true);
      } else {
        setError('The request failed. Check your connection and try again.');
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grain flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent font-display text-[16px] font-bold text-white">
            T
          </div>
          <div className="leading-tight">
            <div className="font-display text-[16px] font-semibold text-ink">TradingTips</div>
            <div className="font-mono text-xs uppercase tracking-[0.16em] text-muted">
              Lead Ops Console
            </div>
          </div>
        </div>

        {sent ? (
          <Card>
            <CardHeader>
              <CardTitle>Check your email</CardTitle>
              <CardDescription>If that account exists, a reset link has been sent.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-[13px] text-muted">
                  The link opens the reset form and expires after a short time. Check the spam
                  folder if it does not arrive.
                </p>
                <div className="w-full pt-1">
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={() => navigate('/login')}
                  >
                    Back to sign in
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setSent(false)}
                >
                  Request another link
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Forgot password</CardTitle>
              <CardDescription>
                Enter your admin account email — we&rsquo;ll send a reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-4">
                  {error !== null && (
                    <p role="alert" className="text-[13px] text-destructive">
                      {error}
                    </p>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setHint(null);
                      }}
                      aria-invalid={hint !== null}
                      aria-describedby={hint !== null ? 'forgot-email-hint' : undefined}
                      required
                      autoFocus
                    />
                    {hint !== null && (
                      <p id="forgot-email-hint" className="text-[13px] text-destructive">
                        {hint}
                      </p>
                    )}
                  </div>
                  <div className="w-full pt-1">
                    <Button type="submit" variant="primary" className="w-full" disabled={pending}>
                      {pending ? 'Sending...' : 'Send reset link'}
                    </Button>
                  </div>
                  <div className="pt-1 text-center">
                    <Link
                      to="/login"
                      className="text-[13px] text-muted underline underline-offset-4 hover:text-ink"
                    >
                      Back to sign in
                    </Link>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
